/**
 * Knowledge Gaps API service.
 *
 * Communicates with the backend knowledge-gap analysis pipeline endpoints
 * at /api/evals/knowledge-gaps/{org_id}/...
 */
import apiClient, { type ApiResponse, type RequestOptions } from './client';
import type { SidebarKnowledge } from './publicInterview';

// ─── Request types ──────────────────────────────────────────────────────────

export type KGSourceType = 'real_session' | 'simulated';

export interface KGRunTriggerRequest {
  max_traces: number;
  skip_consolidation: boolean;
  /** Defaults to 'real_session' on the backend when omitted. */
  source_type?: KGSourceType;
  /** Required when source_type = 'simulated'; must be omitted otherwise. */
  source_batch_id?: string;
}

export interface KGConsolidateRequest {
  since?: string;
  reprocess_all: boolean;
}

export interface KGSummariesParams {
  /**
   * Single status or comma-separated list
   * (e.g. `'active,resolving_via_prompt'`). Backend splits on commas.
   */
  status?: KGSummaryStatus | string;
  category?: string;
  resolution_type?: KGResolutionType;
  sort_by?: 'occurrence_count_desc' | 'first_seen_desc';
  include_evidence?: boolean;
  /** Filter to a single source bucket. Omit to return both. */
  source_type?: KGSourceType;
  page?: number;
  limit?: number;
}

export interface KGRunsParams {
  page?: number;
  limit?: number;
  /** Filter to a single source bucket. Omit to return both. */
  source_type?: KGSourceType;
}

export interface KGRawFindingsParams {
  page?: number;
  limit?: number;
}

// ─── Response types ─────────────────────────────────────────────────────────

export interface KGTriggerResponse {
  run_id: string;
  organization_id: string;
  status: string;
  message: string;
}

export interface KGRunResponse {
  id: string;
  organization_id: string;
  trigger_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  source_type: KGSourceType;
  source_batch_id: string | null;
  trace_window_start: string | null;
  trace_window_end: string | null;
  traces_fetched: number;
  traces_flagged: number;
  traces_analyzed: number;
  findings_created: number;
  consolidation_status: string | null;
  summaries_created: number;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  processed_trace_ids: string[] | null;
  started_at: string | null;
  completed_at: string | null;
  last_trace_created_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface KGRunListResponse {
  runs: KGRunResponse[];
  total: number;
}

export interface KGFindingData {
  finding_type: string;
  topic: string;
  evidence: string;
  details: string;
  suggested_action?: string;
}

export interface KGRawFindingResponse {
  id: string;
  organization_id: string;
  run_id: string;
  trace_id: string;
  trace_created_at: string | null;
  finding_data: KGFindingData;
  is_consolidated: boolean;
  created_at: string;
  session_id: string | null;
  /** Inherited from the parent run; populated by the findings endpoint. */
  source_type: KGSourceType | null;
  source_batch_id: string | null;
  provenance_type?: 'session' | 'simulation' | 'trace';
  provenance_label?: string | null;
  eval_batch_id?: string | null;
  eval_batch_name?: string | null;
  eval_dataset_name?: string | null;
}

export interface KGRawFindingListResponse {
  findings: KGRawFindingResponse[];
  total: number;
}

export type KGResolutionType =
  | 'prompt_update'
  | 'other'
  | 'config_change'
  | 'product_capture';

export type KGSummaryStatus =
  | 'active'
  | 'dismissed'
  | 'resolved'
  | 'resolving_via_prompt'
  | 'resolving_via_product_capture';

export interface KGInProgressPromptUpdate {
  batch_id: string;
  status: string;
  started_at: string;
}

export interface KGLastPromptUpdateOutcome {
  status: 'no_changes' | 'failed';
  batch_id: string;
  failed_at: string;
  error: string | null;
}

export type KGCaptureSessionStatus =
  | 'launching'
  | 'active'
  | 'processing'
  | 'complete'
  | 'failed'
  | 'abandoned';

/**
 * In-flight product-capture state for a knowledge gap, returned on
 * KGSummaryResponse when the gap has a live linked Demo Studio session.
 * `null` when no live session is linked.
 */
export interface KGInFlightCapture {
  session_id: string;
  status: KGCaptureSessionStatus;
  started_at: string;
  completed_at: string | null;
  organization_video_id: string | null;
  video_ready: boolean;
  error_message: string | null;
  live_view_url: string | null;
}

export type KGAssetType = 'video' | 'presentation_deck';

export interface KGAssetRef {
  asset_type: KGAssetType;
  asset_id: string;
}

export interface KGSummaryResponse {
  id: string;
  organization_id: string;
  topic: string;
  category: string;
  summary: string;
  occurrence_count: number;
  raw_finding_ids: string[] | null;
  findings?: KGRawFindingResponse[] | null;
  suggested_action: string | null;
  source_type: KGSourceType;
  status: KGSummaryStatus;
  first_seen_at: string | null;
  last_seen_at: string | null;
  dismissed_at: string | null;
  resolved_at: string | null;
  // Classification of where this gap's fix lives (nullable until backfill lands)
  resolution_type: KGResolutionType | null;
  resolution_type_confidence: number | null;
  // Prompt-update-resolution attribution (nullable for manual/legacy resolutions)
  resolved_by_user_id: string | null;
  resolved_by_prompt_version_id: string | null;
  resolved_by_optimization_batch_id: string | null;
  // Integer version number for the prompt version referenced by
  // resolved_by_prompt_version_id — server-side enriched via JOIN so the
  // resolved-banner modal can call /prompt-versions/.../diff directly
  // without an intermediate UUID→int lookup round-trip.
  resolved_by_prompt_version_number: number | null;
  // Server-enriched display name for resolved_by_user_id (null when the
  // user was deleted or can't be resolved).
  resolved_by_display_name: string | null;
  // Asset-attached resolution attribution (nullable for prompt-update /
  // manual / dismissed rows). When set, the resolved banner renders
  // "Resolved with: <asset title>" instead of the diff link.
  resolved_by_organization_video_id: string | null;
  resolved_by_presentation_deck_id: string | null;
  resolved_by_asset_type: KGAssetType | null;
  resolved_by_asset_title: string | null;
  // Training-session resolution attribution. Populated when an
  // `unanswered_question` gap is resolved by an authenticated user
  // answering it through the in-platform training modal. The resolved
  // banner branches on this to render "Resolved via training session".
  resolved_by_training_session_id: string | null;
  // Server-side enriched FK — interviewer that owns the resolving training
  // session. Used by the resolved banner to fetch the chat transcript via
  // /interviewers/{id}/sessions/{id}.
  resolved_by_training_session_interviewer_id: string | null;
  // Auto-regen-produced prompt version for training-resolved gaps. When
  // populated, the resolved banner can render a prior→after diff modal the
  // same way it does for prompt-update-resolved gaps. The version number is
  // server-side enriched via JOIN; mirrors resolved_by_prompt_version_number.
  auto_regen_prompt_version_id: string | null;
  auto_regen_prompt_version_number: number | null;
  // In-flight prompt optimization batch for this summary (null when none running)
  in_progress_prompt_update: KGInProgressPromptUpdate | null;
  // Outcome of the most recent prompt-update attempt (null when no failed
  // attempt is outstanding). Drives State 5 on the detail panel.
  last_prompt_update_outcome: KGLastPromptUpdateOutcome | null;
  // In-flight product-capture state for this summary (null when no live
  // linked Demo Studio session exists). Drives the "Capture in progress" /
  // "Capture ready" state cards on the gap detail panel.
  in_flight_capture: KGInFlightCapture | null;
  created_at: string;
  updated_at: string;
}

export interface KGSummaryListResponse {
  summaries: KGSummaryResponse[];
  total: number;
}

export interface KGReportCategory {
  category: string;
  count: number;
  total_occurrences: number;
}

export interface KGReportBySource {
  real_session: number;
  simulated: number;
}

export interface KGReportResponse {
  organization_id: string;
  total_active_summaries: number;
  categories: KGReportCategory[];
  /** Active summary count by source bucket — drives the sidebar count badge. */
  by_source: KGReportBySource;
  latest_run: KGRunResponse | null;
}

// ─── Training-session resolution flow types ────────────────────────────────

export interface KGTopicInfo {
  index: number;
  id: string;
  name: string;
}

/**
 * Bootstrap payload returned by `POST /summaries/{id}/start-training-session`.
 *
 * Mirrors the public `/start` endpoint response so the existing
 * `usePublicInterview` chat hook can seed itself directly off this payload
 * without a second resolve/start round-trip.
 */
export interface KGStartTrainingSessionResponse {
  interviewer_token: string;
  interviewer_name: string | null;
  interviewer_description: string | null;
  session_id: string;
  first_question: string;
  suggested_answers: string[];
  suggestion_selection_type: 'single' | 'multi';
  total_topics: number;
  current_topic_index: number;
  topics: KGTopicInfo[];
  topic_confidence: Record<string, number>;
  sidebar_knowledge: SidebarKnowledge | null;
}

// ─── Service ────────────────────────────────────────────────────────────────

const BASE = '/api/evals/knowledge-gaps';

const knowledgeGapsService = {
  /** High-level report with category counts and latest run info. */
  getReport: (orgId: string): Promise<ApiResponse<KGReportResponse>> =>
    apiClient.get<KGReportResponse>(`${BASE}/${orgId}/report`),

  /** Paginated consolidated summaries with optional filters. */
  getSummaries: (
    orgId: string,
    params?: KGSummariesParams,
    options?: RequestOptions,
  ): Promise<ApiResponse<KGSummaryListResponse>> =>
    apiClient.get<KGSummaryListResponse>(
      `${BASE}/${orgId}/summaries`,
      params,
      options,
    ),

  /**
   * Fetch a single summary by id, regardless of status. Used as a fallback by
   * the detail panel when the selected summary has dropped off the list
   * filter (e.g. just transitioned to "resolved").
   */
  getSummary: (
    orgId: string,
    summaryId: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.get<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}`,
      undefined,
      options,
    ),

  /** Raw findings that contributed to a specific summary. */
  getSummaryFindings: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGRawFindingResponse[]>> =>
    apiClient.get<KGRawFindingResponse[]>(
      `${BASE}/${orgId}/summaries/${summaryId}/findings`,
    ),

  /** Dismiss a summary (sets status to "dismissed"). */
  dismissSummary: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.patch<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/dismiss`,
    ),

  /** Resolve a summary (sets status to "resolved"). */
  resolveSummary: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.patch<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/resolve`,
    ),

  /** Trigger a full analysis pipeline run. */
  triggerRun: (
    orgId: string,
    data: KGRunTriggerRequest,
  ): Promise<ApiResponse<KGTriggerResponse>> =>
    apiClient.post<KGTriggerResponse>(`${BASE}/${orgId}/runs`, data),

  /** Trigger standalone re-consolidation. */
  triggerConsolidation: (
    orgId: string,
    data: KGConsolidateRequest,
  ): Promise<ApiResponse<KGTriggerResponse>> =>
    apiClient.post<KGTriggerResponse>(`${BASE}/${orgId}/consolidate`, data),

  /** Paginated list of pipeline runs. */
  getRuns: (
    orgId: string,
    params?: KGRunsParams,
  ): Promise<ApiResponse<KGRunListResponse>> =>
    apiClient.get<KGRunListResponse>(`${BASE}/${orgId}/runs`, params),

  /** Single run details. */
  getRun: (
    orgId: string,
    runId: string,
  ): Promise<ApiResponse<KGRunResponse>> =>
    apiClient.get<KGRunResponse>(`${BASE}/${orgId}/runs/${runId}`),

  /** Paginated list of all raw findings (debug/audit). */
  getRawFindings: (
    orgId: string,
    params?: KGRawFindingsParams,
  ): Promise<ApiResponse<KGRawFindingListResponse>> =>
    apiClient.get<KGRawFindingListResponse>(
      `${BASE}/${orgId}/raw-findings`,
      params,
    ),

  /** Kick off an auto-apply prompt-update optimization run for a prompt-fixable gap. */
  resolveWithPromptUpdate: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGResolveWithPromptUpdateResponse>> =>
    apiClient.post<KGResolveWithPromptUpdateResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/resolve-with-prompt-update`,
    ),

  /**
   * Ranked list of org assets that may address the given gap. Filtered to
   * ready-to-use assets only (videos with segments, decks with slides), and
   * scoped by category (segment_mismatch returns videos only).
   */
  getSuggestedAssets: (
    orgId: string,
    summaryId: string,
    params?: { limit?: number },
    options?: RequestOptions,
  ): Promise<ApiResponse<KGSuggestedAssetsResponse>> =>
    apiClient.get<KGSuggestedAssetsResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/suggested-assets`,
      params,
      options,
    ),

  /**
   * Validate a candidate asset against the gap. Always 200 — uses the
   * `status` field to differentiate `completed` (LLM verdict in hand) from
   * `unavailable` (LLM failed). Either way the response carries the
   * preflight metadata needed to render the confirmation step.
   */
  validateAsset: (
    orgId: string,
    summaryId: string,
    body: KGAssetRef,
    options?: RequestOptions,
  ): Promise<ApiResponse<KGAssetValidationResponse>> =>
    apiClient.post<KGAssetValidationResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/validate-asset`,
      body,
      undefined,
      options,
    ),

  /**
   * Resolve the gap by attaching the given asset. Atomic on the backend —
   * applies any user-consented toggle flip / deck activation in the same
   * transaction as the resolution, then dispatches a prompt-snapshot sync.
   * Returns the refreshed summary with asset attribution populated for the
   * resolved banner.
   */
  resolveWithAsset: (
    orgId: string,
    summaryId: string,
    body: KGResolveWithAssetRequest,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.post<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/resolve-with-asset`,
      body,
    ),

  /**
   * Batch-validate up to 20 assets against the gap in one round trip.
   * Same per-row semantics as `validateAsset` — top-level guard failures
   * (gap not found / not attachable) come back as ApiResponse error;
   * per-row failures land in `results[*].error`.
   */
  validateAssetsBatch: (
    orgId: string,
    summaryId: string,
    body: KGValidateAssetsBatchRequest,
    options?: RequestOptions,
  ): Promise<ApiResponse<KGValidateAssetsBatchResponse>> =>
    apiClient.post<KGValidateAssetsBatchResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/validate-assets`,
      body,
      undefined,
      options,
    ),

  /**
   * Bootstrap a focused training session for an `unanswered_question` gap.
   *
   * Validates eligibility server-side (gap must be active, category =
   * unanswered_question, resolution_type = other), creates a session against
   * the org's primary interviewer with a one-question rubric, and returns a
   * payload the FE chat modal can seed itself with directly. On session
   * completion the server marks the linked gap resolved with
   * training-session attribution.
   */
  startTrainingSession: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGStartTrainingSessionResponse>> =>
    apiClient.post<KGStartTrainingSessionResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/start-training-session`,
    ),

  /**
   * Launch a Demo Studio session linked to a `product_capture` gap.
   *
   * Server-side: atomically flips the gap to `resolving_via_product_capture`,
   * creates the demo studio session with the FK populated, and returns the
   * session id + live view URL. File attachments are uploaded afterwards via
   * the existing `POST /api/demo-studio/sessions/{id}/upload-file` endpoint.
   */
  startProductCapture: (
    orgId: string,
    summaryId: string,
    body: KGStartProductCaptureRequest,
  ): Promise<ApiResponse<KGStartProductCaptureResponse>> =>
    apiClient.post<KGStartProductCaptureResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/start-product-capture`,
      body,
    ),

  /**
   * Apply the captured video from the linked Demo Studio session to resolve
   * the gap. The video to apply is server-determined from the linked
   * session — no body required. Approves all segments produced by the
   * capture and fires a single unconditional prompt-snapshot sync.
   */
  resolveWithProductCapture: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.post<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/resolve-with-product-capture`,
    ),

  /**
   * Discard the in-flight product capture for a gap. Unlinks the session
   * and reverts the gap from `resolving_via_product_capture` to `active`.
   * Does NOT touch the Demo Studio session lifecycle — the unlinked
   * session keeps running and the user manages it from the Demo Studio
   * surface.
   */
  cancelInFlightCapture: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.delete<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/in-flight-capture`,
    ),
};

// ─── Product-capture flow types ────────────────────────────────────────────

export interface KGStartProductCaptureRequest {
  title: string;
  initial_url: string;
}

export interface KGStartProductCaptureResponse {
  session_id: string;
  status: string;
  live_view_url: string | null;
  created_at: string | null;
}

export interface KGResolveWithPromptUpdateResponse {
  batch_id: string;
  status: string;
  message?: string;
}

// ─── Asset-attachment flow types ────────────────────────────────────────────

export interface KGRankedAsset {
  asset_ref: KGAssetRef;
  title: string;
  /**
   * Non-negative score; magnitude is only meaningful within a single ranking
   * call, never across calls.
   */
  relevance_score: number;
  summary: string | null;
  is_ready: boolean;
}

export interface KGSuggestedAssetsResponse {
  assets: KGRankedAsset[];
}

export interface KGValidationSnippet {
  text: string;
  source_location: string | null;
}

export type KGRequiredToggleKey = 'use_all_videos' | 'use_active_deck';

export interface KGToggleState {
  key: KGRequiredToggleKey;
  currently_enabled: boolean;
}

export type KGValidationStatus = 'completed' | 'unavailable';
export type KGValidationVerdict = 'addresses' | 'partial' | 'not_relevant';

export interface KGAssetValidationResponse {
  /**
   * `completed` — LLM verdict + snippets present.
   * `unavailable` — LLM call failed (timeout / parse error). `verdict` and
   * `confidence` are null but `required_toggle` and `deck_activation_needed`
   * are still populated so the confirmation step can render.
   */
  status: KGValidationStatus;
  verdict: KGValidationVerdict | null;
  confidence: number | null;
  snippets: KGValidationSnippet[];
  rationale: string;
  cached: boolean;
  asset_ref: KGAssetRef;
  required_toggle: KGToggleState;
  /** True iff the candidate asset is a non-active deck. Always false for videos. */
  deck_activation_needed: boolean;
}

export interface KGResolveWithAssetRequest {
  asset_ref: KGAssetRef;
  /** Flip the relevant toggle (use_all_videos / use_active_deck) on if it's off. */
  enable_toggle: boolean;
  /** For decks only: mark the deck active so the runtime can read it. */
  activate_deck: boolean;
}

export interface KGValidateAssetsBatchRequest {
  /** 1..20 asset refs. Server caps at 20; client passes the suggested-list slice. */
  asset_refs: KGAssetRef[];
}

export interface KGValidateAssetsBatchResultRow {
  asset_ref: KGAssetRef;
  /** Populated on success. Same shape as the single-asset response. */
  result: KGAssetValidationResponse | null;
  /** Populated when this row failed. Mutually exclusive with `result`. */
  error: { code: KGAssetErrorCode; message: string } | null;
}

export interface KGValidateAssetsBatchResponse {
  results: KGValidateAssetsBatchResultRow[];
}

/**
 * Server error codes returned in `ApiResponse.code` for asset-flow endpoints.
 * The frontend can branch on these to render targeted UX.
 */
export type KGAssetErrorCode =
  | 'summary_not_found'
  | 'asset_not_found'
  | 'cross_org_forbidden'
  | 'gap_not_attachable'
  | 'invalid_asset_for_category'
  | 'asset_not_ready'
  | 'agent_not_configured'
  | 'asset_would_be_ignored'
  | 'gap_state_conflict'
  | 'internal_error';

export default knowledgeGapsService;
